import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCcw, TrendingUp, MousePointerClick, FileCheck, Link2, BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function FunnelStage({ icon: Icon, label, value, accent, helper }) {
  const accents = {
    cyan: 'border-[#00F0FF]/40 bg-[#00F0FF]/[0.04] text-[#00F0FF]',
    green: 'border-[#00FF94]/40 bg-[#00FF94]/[0.04] text-[#00FF94]',
    amber: 'border-amber-500/40 bg-amber-500/[0.04] text-amber-400',
    neutral: 'border-white/15 bg-white/[0.03] text-neutral-300',
  };
  return (
    <div className={`p-5 rounded-xl border ${accents[accent] || accents.neutral}`}>
      <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase mb-2 opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-3xl sm:text-4xl font-bold text-white leading-tight">{value}</div>
      {helper ? <p className="text-xs text-neutral-400 mt-1.5">{helper}</p> : null}
    </div>
  );
}

export default function AdminOptimizerFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/optimizer-funnel?days=${days}`, {
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load funnel');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <AdminLayout>
      <div className="px-6 py-8" data-testid="admin-optimizer-funnel">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-[#00F0FF]" />
              Optimizer funnel
            </h1>
            <p className="text-neutral-400 text-sm mt-1 max-w-2xl">
              How the public /optimizer lead magnet converts paste → click → application. Use this to iterate
              on the teaser copy and CTA placement.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="optimizer-funnel-refresh"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              data-testid={`optimizer-funnel-window-${opt.value}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${
                days === opt.value
                  ? 'bg-[#00F0FF]/15 border-[#00F0FF]/60 text-[#00F0FF]'
                  : 'bg-white/[0.02] border-white/10 text-neutral-300 hover:border-white/30'
              }`}
            >
              Last {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <div className="py-20 text-center text-neutral-500">No data yet — share the /optimizer link to start collecting.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <FunnelStage
                icon={Link2}
                label="Previews run"
                value={data.previews_total.toLocaleString()}
                helper={`${data.unique_urls} unique URLs pasted`}
                accent="cyan"
              />
              <FunnelStage
                icon={MousePointerClick}
                label="Submit-CTA clicks"
                value={data.submit_cta_clicks.toLocaleString()}
                helper={`${data.rates.click_through_pct}% click-through`}
                accent="amber"
              />
              <FunnelStage
                icon={FileCheck}
                label="VIP applications"
                value={data.vip_applications_in_window.toLocaleString()}
                helper={`${data.rates.preview_to_app_pct}% preview-to-app rate`}
                accent="green"
              />
              <FunnelStage
                icon={TrendingUp}
                label="Window"
                value={`${data.window_days}d`}
                helper="Active selection"
              />
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-6">
              <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#00F0FF]" />
                Most-pasted URLs
              </h2>
              {data.top_urls.length === 0 ? (
                <p className="text-sm text-neutral-500">No URLs pasted yet in this window.</p>
              ) : (
                <ul className="space-y-2">
                  {data.top_urls.map((row, i) => (
                    <li
                      key={row.product_url}
                      className="flex items-center justify-between gap-3 p-2 bg-black/40 border border-white/5 rounded-lg"
                      data-testid={`top-url-row-${i}`}
                    >
                      <a
                        href={row.product_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-[#00F0FF] hover:underline truncate"
                      >
                        {row.product_url}
                      </a>
                      <span className="text-xs font-bold text-neutral-300 flex-shrink-0">
                        {row.count}× pasted
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="text-xs text-neutral-500 leading-relaxed">
              Note: VIP applications are counted by date-range only — we don't have a 1:1 attribution between
              an optimizer paste and the final application (both flows are anonymous). The
              preview-to-app rate is directional. To get exact attribution, add a hidden referrer field on
              the /submit form pointing at the input_hash.
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
